import { createContext, useContext, useEffect } from "react";
import { useFilmmaker } from "../hooks/useFilmmaker";

const FilmmakerScopeContext = createContext({ slug: null, filmmaker: null, isFilmmakerMode: false, loading: true });

const DEFAULT_ACCENT = "201 169 97";       // gold
const DEFAULT_ACCENT_DEEP = "154 126 63";  // antique gold
const DEFAULT_ACCENT_SOFT = "229 213 176"; // champagne

function hexToTriplet(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

function tripletToString(t) {
  return `${t.r} ${t.g} ${t.b}`;
}

function darken({ r, g, b }, amount = 0.28) {
  return {
    r: Math.max(0, Math.floor(r * (1 - amount))),
    g: Math.max(0, Math.floor(g * (1 - amount))),
    b: Math.max(0, Math.floor(b * (1 - amount))),
  };
}

function lighten({ r, g, b }, amount = 0.35) {
  return {
    r: Math.min(255, Math.floor(r + (255 - r) * amount)),
    g: Math.min(255, Math.floor(g + (255 - g) * amount)),
    b: Math.min(255, Math.floor(b + (255 - b) * amount)),
  };
}

export function FilmmakerScopeProvider({ children }) {
  const value = useFilmmaker();

  useEffect(() => {
    const root = document.documentElement;
    const accentHex = value.isFilmmakerMode && value.filmmaker?.accent_color;
    const t = accentHex ? hexToTriplet(accentHex) : null;
    if (t) {
      root.style.setProperty("--gs-accent", tripletToString(t));
      root.style.setProperty("--gs-accent-deep", tripletToString(darken(t, 0.3)));
      root.style.setProperty("--gs-accent-soft", tripletToString(lighten(t, 0.5)));
    } else {
      root.style.setProperty("--gs-accent", DEFAULT_ACCENT);
      root.style.setProperty("--gs-accent-deep", DEFAULT_ACCENT_DEEP);
      root.style.setProperty("--gs-accent-soft", DEFAULT_ACCENT_SOFT);
    }
  }, [value.isFilmmakerMode, value.filmmaker]);

  return <FilmmakerScopeContext.Provider value={value}>{children}</FilmmakerScopeContext.Provider>;
}

export function useFilmmakerScope() {
  return useContext(FilmmakerScopeContext);
}
